/**
 * PageLoadAnimation component to display a loading animation during the initial page load.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.setTextAnimation - A function to set the text animation.
 * @param {Object} props.data - Data object containing information for the animation.
 * @param {boolean} props.isHeroVideoLoaded - A boolean indicating whether the hero video is loaded.
 * @returns {JSX.Element|null} Returns the PageLoadAnimation component.
 */

import React, { useEffect, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import styles from "./pageLoadAnimation.module.scss";

function PageLoadAnimation({ setTextAnimation, data, isHeroVideoLoaded }) {
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const animationControls = useAnimation();

  // To prevent the body scroll when loading animation happens
  const addHeightAndOverflowToBody = () => {
    document.body.style.overflowY = "hidden";
    document.body.style.height = "100vh";
  };

  const removeHeightAndOverflowToBody = () => {
    document.body.style.overflowY = "";
    document.body.style.height = "";
  };

  useEffect(() => {
    addHeightAndOverflowToBody();
  }, []);

  useEffect(() => {
    if (isHeroVideoLoaded) {
      animationControls.start("hidden");
    }
  }, [isHeroVideoLoaded]);

  const onComplete = () => {
    setIsAnimationComplete(true);
    setTextAnimation(true);
    removeHeightAndOverflowToBody();
  };

  if (isAnimationComplete) return null;

  return (
    <motion.div
      className={styles.container}
      variants={{
        hidden: { opacity: 1, scale: 8 },
        visible: { opacity: 1 },
      }}
      initial="visible"
      animate={animationControls}
      transition={{ duration: 1.5, ease: "easeOut", delay: 1 }}
      onAnimationComplete={onComplete}
    >
      <img
        src={`https:${data?.initialPageLoadBackgroundImage?.fields?.file?.url}`}
        alt={data?.image?.fields?.title}
        className={styles.loaderImg}
      />
    </motion.div>
  );
}

export default PageLoadAnimation;
